import { EventData } from './types';
import { PII_PATTERNS, STORAGE_KEYS } from './constants';
export class PrivacyManager {
    private optOut: boolean = false;
    constructor() {
        // Load opt-out preference from localStorage
        this.loadOptOutPreference();
    }
    /**
     * Check if tracking is allowed based on opt-out and DNT settings
     */
    isTrackingAllowed(): boolean {
        if (this.optOut) {
            return false;
        }
        if (this.isDNTEnabled()) {
            return false;
        }
        return true;
    }
    /**
     * Set user opt-out preference
     */
    setOptOut(optOut: boolean): void {
        this.optOut = optOut;
        this.saveOptOutPreference();
    }
    /**
     * Get user opt-out preference
     */
    getOptOut(): boolean {
        return this.optOut;
    }
    /**
     * Check if Do Not Track (DNT) is enabled in browser
     */
    isDNTEnabled(): boolean {
        // Check various DNT properties for cross-browser compatibility
        const dnt = navigator.doNotTrack ||
            (window as any).doNotTrack ||
            (navigator as any).msDoNotTrack;
        return dnt === '1' || dnt === 'yes';
    }
    /**
     * Anonymize user identifier using SHA-256 hashing
     */
    async anonymizeUserId(userId: string): Promise<string> {
        try {
            // Use Web Crypto API for secure hashing
            const encoder = new TextEncoder();
            const data = encoder.encode(userId);
            const hashBuffer = await crypto.subtle.digest('SHA-256', data);
            // Convert hash to hex string
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
            return hashHex;
        }
        catch (error) {
            console.error('[PrivacyManager] Error anonymizing user ID:', error);
            // Fallback to simple hash if Web Crypto API fails
            return this.simpleHash(userId);
        }
    }
    /**
     * Validate event data for PII and sanitize if found
     */
    sanitizeEventData(data: EventData): EventData {
        const sanitized: EventData = { ...data };
        let piiDetected = false;
        for (const [key, value] of Object.entries(sanitized)) {
            if (typeof value === 'string') {
                let sanitizedValue = value;
                // Check for email
                if (PII_PATTERNS.email.test(value)) {
                    sanitizedValue = sanitizedValue.replace(PII_PATTERNS.email, '[REDACTED_EMAIL]');
                    piiDetected = true;
                }
                // Check for phone
                if (PII_PATTERNS.phone.test(value)) {
                    sanitizedValue = sanitizedValue.replace(PII_PATTERNS.phone, '[REDACTED_PHONE]');
                    piiDetected = true;
                }
                // Check for SSN
                if (PII_PATTERNS.ssn.test(value)) {
                    sanitizedValue = sanitizedValue.replace(PII_PATTERNS.ssn, '[REDACTED_SSN]');
                    piiDetected = true;
                }
                // Check for credit card
                if (PII_PATTERNS.creditCard.test(value)) {
                    sanitizedValue = sanitizedValue.replace(PII_PATTERNS.creditCard, '[REDACTED_CC]');
                    piiDetected = true;
                }
                if (sanitizedValue !== value) {
                    sanitized[key] = sanitizedValue;
                }
            }
            else if (typeof value === 'object' && value !== null) {
                // Recursively sanitize nested objects
                sanitized[key] = this.sanitizeEventData(value);
            }
        }
        if (piiDetected) {
        }
        return sanitized;
    }
    /**
     * Load opt-out preference from localStorage
     */
    private loadOptOutPreference(): void {
        try {
            const stored = localStorage.getItem(STORAGE_KEYS.OPT_OUT);
            if (stored !== null) {
                this.optOut = stored === 'true';
            }
        }
        catch (error) {
            console.error('[PrivacyManager] Error loading opt-out preference:', error);
        }
    }
    /**
     * Save opt-out preference to localStorage
     */
    private saveOptOutPreference(): void {
        try {
            localStorage.setItem(STORAGE_KEYS.OPT_OUT, String(this.optOut));
        }
        catch (error) {
            console.error('[PrivacyManager] Error saving opt-out preference:', error);
        }
    }
    /**
     * Simple hash function as fallback
     */
    private simpleHash(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(16);
    }
    /**
     * Clear all privacy-related data from localStorage
     */
    clearPrivacyData(): void {
        try {
            localStorage.removeItem(STORAGE_KEYS.OPT_OUT);
            this.optOut = false;
        }
        catch (error) {
            console.error('[PrivacyManager] Error clearing privacy data:', error);
        }
    }
}
